import React, { useEffect, useMemo, useRef, useState } from 'react'
import { GroupItem } from '../../../../typings'
import { useLocalizedContent } from '../../hooks/useLocalizedContent'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  clearHandledError,
  setHandledError,
} from '../../store/slices/handledErrorSlice'
import {
  toggleCloseGroup,
  toggleFocusOnCoverImageEditButton,
  toggleUpdateGroup,
} from '../../store/slices/sheetsSlice'
import SheetWrapper from '../../components/SheetWrapper'
import UpdateGroup from './UpdateGroup'
import UpdateGroupFooter from './components/UpdateGroupFooter'
import CloseGroupActions from './components/CloseGroupActions'
import { CloseGroup } from './CloseGroup'
import useWindowSize from '../../utils/useWindowSize'
import { useGetEligibilityQuery } from '../../services/checkEligibility'
import { closeGroupContainer } from './index.css'
import { showGlobalToast } from '../../store/slices/globalToastSlice'
import { useUpdateGroupMutation } from '../../services/updateGroupApi'
import { useCloseGroupMutation } from '../../services/closeGroupApi'
import { createGroupPayload } from '../../utils/groupForm/createPayload'
import { clearForm, selectGroupForm } from '../../store/slices/groupFormSlice'
import { tracking } from '../../lib/analytics/fpti'
import Events from '../../lib/analytics/eventTags.js'
import serverData from '../../utils/server-data'
import { isCoverOnCreateFlowEnabled } from '../../utils/features'

interface UpdateProps {
  groupItem: GroupItem
}

const Update = ({ groupItem }: UpdateProps) => {
  const { getContent: getLocalizedContent } = useLocalizedContent()
  const dispatch = useAppDispatch()
  const [openedCloseGroup, setOpenedCloseGroup] = useState(false)
  const openCloseGroupButton = useRef<HTMLButtonElement>()
  const isUpdateGroupOpened = useAppSelector(({ sheets }) => sheets.updateGroup)
  const isCloseGroupOpened = useAppSelector(({ sheets }) => sheets.closeGroup)
  const focusOnCoverImageEditButton = useAppSelector(
    ({ sheets }) => sheets.focusOnCoverImageEditButton,
  )
  const { isSmallScreen } = useWindowSize()
  const { isFetching } = useGetEligibilityQuery(null)
  const [updateGroupMutation, { isLoading: isUpdatingGroup }] =
    useUpdateGroupMutation()
  const [closeGroupMutation, { isLoading: isClosingGroup }] =
    useCloseGroupMutation()
  const isLoading = useMemo(() => {
    if (isFetching) {
      return true
    } else if (isUpdateGroupOpened) {
      return isUpdatingGroup
    } else if (isCloseGroupOpened) {
      return isClosingGroup
    }

    return false
  }, [
    isFetching,
    isUpdateGroupOpened,
    isCloseGroupOpened,
    isUpdatingGroup,
    isClosingGroup,
  ])
  const groupForm = useAppSelector(selectGroupForm)

  useEffect(() => {
    return () => {
      dispatch(toggleFocusOnCoverImageEditButton(false))
    }
  }, [])

  useEffect(() => {
    if (openedCloseGroup && openCloseGroupButton.current) {
      openCloseGroupButton.current.focus()
      setOpenedCloseGroup(false)
    }
  }, [openCloseGroupButton.current])

  const updateGroup = async () => {
    tracking(
      Events.p2p_pools_edit_update_pressed({
        source: serverData.trackingParameters.externalSource,
      }),
    )
    dispatch(clearHandledError())
    const payload = {
      id: groupItem.id,
      ...createGroupPayload(groupForm),
    }
    try {
      await updateGroupMutation(payload).unwrap()
      dispatch(toggleUpdateGroup({ focusOnCoverImageEditButton: false }))
      dispatch(
        showGlobalToast({
          isSuccess: true,
          message: getLocalizedContent('update.succesMessage'),
        }),
      )
      tracking(
        Events.p2p_pools_edit_success({
          source: serverData.trackingParameters.externalSource,
          page_variant: 'creator',
          pool_id: groupItem.id,
        }),
      )
    } catch (error) {
      tracking(
        Events.p2p_failed_customer_interaction_occurred({
          source: serverData.trackingParameters.externalSource,
          error_code: error.data?.error?.code,
        }),
      )
      dispatch(setHandledError(error)(dispatch))
    }
  }

  const closeGroup = async (groupId: string) => {
    tracking(
      Events.p2p_pools_edit_close_confirm_pressed({
        source: serverData.trackingParameters.externalSource,
        pool_id: groupId,
      }),
    )
    dispatch(clearHandledError())
    try {
      await closeGroupMutation(groupId).unwrap()
      dispatch(toggleCloseGroup())
      dispatch(
        showGlobalToast({
          isSuccess: true,
          message: getLocalizedContent('update.closeGroup.successMessage'),
        }),
      )
    } catch (error) {
      dispatch(setHandledError(error)(dispatch))
    }
  }

  const handleCloseComplete = () => {
    if (isUpdateGroupOpened) {
      dispatch(toggleUpdateGroup({ focusOnCoverImageEditButton: false }))
    }

    if (isCloseGroupOpened) {
      dispatch(toggleCloseGroup())
    }

    dispatch(clearHandledError())
    dispatch(clearForm())
  }

  const goToCloseGroup = () => {
    dispatch(clearHandledError())
    dispatch(toggleUpdateGroup({ focusOnCoverImageEditButton: false }))
    dispatch(toggleCloseGroup())
    setOpenedCloseGroup(true)
  }

  const goToUpdateGroupFields = () => {
    dispatch(toggleCloseGroup())
    dispatch(toggleUpdateGroup({ focusOnCoverImageEditButton: false }))
  }

  const getFooter = () => {
    if (isLoading) {
      return null
    }

    if (isUpdateGroupOpened) {
      return (
        <UpdateGroupFooter
          isLoading={isUpdatingGroup}
          goToCloseGroup={goToCloseGroup}
          ref={openCloseGroupButton}
        />
      )
    }

    return null
  }

  const getActions = () => {
    if (isCoverOnCreateFlowEnabled() || isUpdateGroupOpened || isLoading) {
      return null
    }

    return <CloseGroupActions backToUpdateGroup={goToUpdateGroupFields} />
  }

  const getContent = () => {
    if (isLoading) {
      return null
    }

    if (isUpdateGroupOpened) {
      return <UpdateGroup onSubmit={updateGroup} />
    }

    return <CloseGroup groupItem={groupItem} onSubmit={closeGroup} />
  }

  const isContentCentered =
    (!isSmallScreen && !isCoverOnCreateFlowEnabled()) || isCloseGroupOpened

  return (
    <SheetWrapper
      center={isContentCentered}
      data-testid="updateGroupSheet"
      onCloseComplete={handleCloseComplete}
      title={getLocalizedContent('update.sheetTitle')}
      hideTitle={true}
      footerContents={getFooter()}
      isOpen={isUpdateGroupOpened || isCloseGroupOpened}
      action={getActions()}
      className={isCloseGroupOpened && !isLoading ? closeGroupContainer : null}
      onBeforeClose={() => !isLoading}
      isLoading={isLoading}
      skipFormFocus={!focusOnCoverImageEditButton}
    >
      {getContent()}
    </SheetWrapper>
  )
}

export default Update
